import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useFocusTrap } from '../hooks/useFocusTrap';
import './UserGuideModal.css';

export interface UserGuideModalProps {
  initialScreen?: 'welcome' | 'guide';
  pinCount?: number;
  onClose: () => void;
  onOpenPrivacy?: () => void;
  onOpenTerms?: () => void;
}

interface Slide {
  icon: string;
  bg: string;
  title: string;
  bullets: string[];
  note?: string;
  special?: 'findFlat';
}

const SLIDES: Slide[] = [
  {
    icon: '📍',
    bg: '#FBEBE4',
    title: 'Pin Your Rent',
    bullets: [
      'Enter an address or drop a pin on the map, then add your rent, BHK, furnishing and other details. That\'s it — no sign-up.',
      'Use the search bar to jump to any neighbourhood and apply filters to narrow results by bedrooms, price, or furnishing.'
    ],
  },
  {
    icon: '🗺️',
    bg: '#E8EDF5',
    title: 'Reading the Map',
    bullets: [
      'When pins overlap they cluster — showing the flat count and average rent for that area. Zoom in to break clusters apart and see individual listings.',
    ],
  },
  {
    icon: '📡',
    bg: '#EEF4E9',
    title: 'Find Rents Near You',
    bullets: [
      'Tap the Near Me button in the header to pinpoint your exact location and instantly see rents nearby.',
    ],
    note: 'Super useful if you\'re standing at a place you love, near your office, or next to a metro station — just tap and see what people pay around you.',
  },
  {
    icon: '📊',
    bg: '#F0EBFC',
    title: 'Area Stats',
    bullets: [
      'Tap the Area Stats button on the right side. Then tap two points on the map to draw a rectangle over any neighbourhood.',
      'You\'ll instantly see a breakdown: average rent by BHK, number of flats — all for that specific area. Great for comparing neighbourhoods.',
    ],
  },
  {
    icon: '🚌',
    bg: '#FBF2E3',
    title: 'Transit Overlay',
    bullets: [
      'Tap the Transit button to overlay all transit lines and stations on the map — instantly see how well-connected any area is.',
    ],
    note: 'Next up: a guide to posting your room, flat, or condo — for both owners and people searching for a tenant.',
  },
  {
    icon: '🏠',
    bg: '#E8EDF5',
    title: 'Find Flat or Tenants',
    bullets: [
      'Looking for a condo? Have one to rent out? Use the Find Condo or Tenants button below the map, or tap Condo Hunt on the right panel.',
      'Pins with a green AVAILABLE tag are actively listed. Drop a seeker pin to get matched!',
    ],
    special: 'findFlat',
  },
];

const OWNER_STEPS = [
  'Drop a pin at your flat\'s location',
  'Fill in the rent details',
  'Tell us if you\'re listing the whole place or finding a flatmate',
  'We\'ll email you when seekers match',
];

const SEEKER_STEPS = [
  'Drop a pin where you want to live',
  'Tell us your budget and what you\'re looking for — a whole flat or a room',
  'We\'ll match you with available flats AND people looking for flatmates nearby. Whatever fits, you\'ll get an email.',
];

export const UserGuideModal: React.FC<UserGuideModalProps> = ({
  initialScreen = 'welcome',
  pinCount = 500,
  onClose,
  onOpenPrivacy,
  onOpenTerms,
}) => {
  const [screen, setScreen] = useState<'welcome' | 'guide'>(initialScreen);
  const [slide, setSlide] = useState(0);
  const [expanded, setExpanded] = useState<'owner' | 'seeker' | null>(null);
  const [copied, setCopied] = useState(false);

  const shareMsg = `${pinCount > 0 ? `${pinCount}+` : 'Hundreds of'} GTA renters already shared their rent anonymously. No login. No registration. Just real data from real people helping each other navigate Toronto's rental market.`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleStart = () => {
    localStorage.setItem('gta-onboarded', 'true');
    onClose();
  };

  const handleLearnHow = () => {
    setScreen('guide');
    setSlide(0);
    setExpanded(null);
  };

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(`${shareMsg} ${window.location.href}`); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShareSite = () => {
    const text = `Check out GTA Rental — real, anonymously-shared rent data for the Greater Toronto Area. No sign-up needed! ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const goSlide = (n: number) => { setSlide(n); setExpanded(null); };
  const current = SLIDES[slide];
  const isLast = slide === SLIDES.length - 1;
  const trapRef = useFocusTrap(true);

  const titleId = screen === 'welcome' ? 'ugm-welcome-title' : `ugm-slide-title-${slide}`;

  const modal = (
    <div className="ugm-backdrop" onClick={screen === 'guide' ? onClose : undefined} aria-hidden="true">
      <div
        className={`ugm-card ugm-card--${screen}`}
        ref={el => { trapRef.current = el; }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
      >

        {/* ──────────────── WELCOME ──────────────── */}
        {screen === 'welcome' && (
          <div className="ugm-welcome">
            <h2 id="ugm-welcome-title" className="ugm-w-heading">We build trust together</h2>

            {/* Trust card */}
            <div className="ugm-w-card ugm-w-card--teal">
              <div className="ugm-w-label">EVERY PIN EARNS ITS PLACE 📍</div>
              <p className="ugm-w-body">
                Anti-manipulation guards keep this map honest. If something looks off —{' '}
                <strong>report it.</strong> You are the fact-checker for your neighbourhood.
              </p>
            </div>

            {/* Spread the word */}
            <h3 className="ugm-w-subheading">Help us spread the word</h3>
            <div className="ugm-w-card ugm-w-card--amber">
              <div className="ugm-w-card-row">
                <span className="ugm-w-label">MESSAGE TO SHARE</span>
                <button className="ugm-w-copy-btn" onClick={handleCopy} aria-label={copied ? 'Copied!' : 'Copy message to clipboard'}>
                  {copied
                    ? <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                    : <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  }
                </button>
              </div>
              <p className="ugm-w-share-text">{shareMsg}</p>
            </div>

            <p className="ugm-w-terms">
              By moving forward to the map, you agree to our{' '}
              <button type="button" className="ugm-w-terms-link" onClick={() => onOpenPrivacy?.()}>Privacy Policy</button>
              {' '}and{' '}
              <button type="button" className="ugm-w-terms-link" onClick={() => onOpenTerms?.()}>Terms of Use</button>
            </p>

            <button className="ugm-w-btn ugm-w-btn--whatsapp" onClick={handleShareSite}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Share on WhatsApp
            </button>
            <button className="ugm-w-btn ugm-w-btn--start" onClick={handleStart}>
              Let's start, I'll keep it honest
            </button>
            <button className="ugm-w-btn ugm-w-btn--ghost" onClick={handleLearnHow}>
              Learn how to use
            </button>
          </div>
        )}

        {/* ──────────────── GUIDE ──────────────── */}
        {screen === 'guide' && (
          <div className="ugm-guide">
            <button className="ugm-g-close" onClick={onClose} aria-label="Close guide">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Slide icon + title */}
            <div className="ugm-g-icon" style={{ background: current.bg }} aria-hidden="true">{current.icon}</div>
            <h2 id={titleId} className="ugm-g-title">{current.title}</h2>

            {/* Bullets */}
            <div className="ugm-g-content">
              {current.bullets.map((b, i) => (
                <div key={i} className="ugm-g-bullet">
                  <span className="ugm-g-dot" />
                  <p>{b}</p>
                </div>
              ))}
              {current.note && <p className="ugm-g-note">{current.note}</p>}

              {/* Find Flat sub-cards */}
              {current.special === 'findFlat' && (
                <div className="ugm-sub-cards">
                  {/* Owner */}
                  <div className={`ugm-sub-card${expanded === 'owner' ? ' ugm-sub-card--open' : ''}`}>
                    <button
                      className="ugm-sub-header"
                      onClick={() => setExpanded(e => e === 'owner' ? null : 'owner')}
                    >
                      <span className="ugm-sub-emoji">🔑</span>
                      <span className="ugm-sub-label">I have a condo / house</span>
                      <span className="ugm-sub-chevron">{expanded === 'owner' ? '▲' : '▼'}</span>
                    </button>
                    {expanded === 'owner' && (
                      <div className="ugm-sub-body">
                        <p className="ugm-sub-tag">Here's how it works</p>
                        <ol className="ugm-steps">
                          {OWNER_STEPS.map((s, i) => <li key={i}>{s}</li>)}
                        </ol>
                      </div>
                    )}
                  </div>

                  {/* Seeker */}
                  <div className={`ugm-sub-card${expanded === 'seeker' ? ' ugm-sub-card--open' : ''}`}>
                    <button
                      className="ugm-sub-header"
                      onClick={() => setExpanded(e => e === 'seeker' ? null : 'seeker')}
                    >
                      <span className="ugm-sub-emoji">🔍</span>
                      <span className="ugm-sub-label">I am looking for a condo / house</span>
                      <span className="ugm-sub-chevron">{expanded === 'seeker' ? '▲' : '▼'}</span>
                    </button>
                    {expanded === 'seeker' && (
                      <div className="ugm-sub-body">
                        <p className="ugm-sub-tag">Here's how it works</p>
                        <ol className="ugm-steps">
                          {SEEKER_STEPS.map((s, i) => <li key={i}>{s}</li>)}
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Dots */}
            <div className="ugm-dots">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  className={`ugm-dot${i === slide ? ' ugm-dot--active' : ''}`}
                  onClick={() => goSlide(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            {/* Nav */}
            <div className="ugm-g-nav">
              {slide > 0
                ? <button className="ugm-nav-btn ugm-nav-btn--back" onClick={() => goSlide(slide - 1)}>← Back</button>
                : <div />
              }
              {isLast
                ? <button className="ugm-nav-btn ugm-nav-btn--done" onClick={onClose}>Done ✓</button>
                : <button className="ugm-nav-btn ugm-nav-btn--next" onClick={() => goSlide(slide + 1)}>Next →</button>
              }
            </div>
          </div>
        )}

      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};
